export const LOADING_INDICATOR_ATTRIBUTE_NAME = 'data-loading-indicator';
export const ERROR_INDICATOR_ATTRIBUTE_NAME = 'data-error-indicator';

export const WAIT_FOR_LOAD_TIMEOUT_MS = 10000;

/**
 * Helper for loading indicators to mark themselves during render. For example:
 *
 *  function MyLoadingIndicator() {
 *    return <div {...loadingIndicatorAttrs()}>Loading...</div>;
 *  }
 *
 * @returns A set of attributes that should be spread into JSX.
 */
export function loadingIndicatorAttrs(): {[key: string]: string} {
  return {[LOADING_INDICATOR_ATTRIBUTE_NAME]: 'true'};
}

/**
 * Helper for error indicators to mark themselves during render. For example:
 *
 *  function MyErrorIndicator() {
 *    return <div {...errorIndicatorAttrs()}>Uh-oh! An error occurred!</div>;
 *  }
 *
 * @returns A set of attributes that should be spread into JSX.
 */
export function errorIndicatorAttrs(): {[key: string]: string} {
  return {[ERROR_INDICATOR_ATTRIBUTE_NAME]: 'true'};
}

/**
 * Queries all loading indicators part of the given root element
 * @param rootElement The root element to search for loading indicators
 * @returns An array of all HTMLElements that have a loading indicator
 */
export function queryAllLoadingIndicators(rootElement: HTMLElement): Array<Element> {
  return Array.from(rootElement.querySelectorAll(`[${LOADING_INDICATOR_ATTRIBUTE_NAME}]`));
}

/**
 * Queries all error indicators part of the given root element
 * @param rootElement The root element to search for error indicators
 * @returns An array of all HTMLElements that have a error indicator
 */
export function queryAllErrorIndicators(rootElement: HTMLElement): Array<Element> {
  return Array.from(rootElement.querySelectorAll(`[${ERROR_INDICATOR_ATTRIBUTE_NAME}]`));
}

/**
 * Waits for all loading indicators to resolve. All loading indicators are resolved when
 * they are removed from the specified rootElement's DOM tree.
 *
 * This function can be used in async functions that block a certain action until all loading indicators resolve.
 * i.e. export a notebook entry after the page is fully loaded
 *
 * @param rootElement The root element to search for loading indicators
 * @returns A promise that resolves to true when all loading indicators have been removed from the specified rootElement.
 * If after WAIT_FOR_LOAD_TIMEOUT_MS timeout and there are still loading indicators, the promise will resolve to false.
 */
export function waitForLoadingIndicatorsToResolve(rootElement: HTMLElement): Promise<boolean> {
  let mutationObserver: MutationObserver | null = null;
  if (rootElement == null) {
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    setTimeout(() => {
      mutationObserver?.disconnect();
      resolve(false);
    }, WAIT_FOR_LOAD_TIMEOUT_MS);

    const loadingIndicators = queryAllLoadingIndicators(rootElement);
    if (loadingIndicators.length === 0) {
      resolve(true);
    }

    mutationObserver = new MutationObserver((_, observer) => {
      if (queryAllLoadingIndicators(rootElement).length === 0) {
        observer.disconnect();
        resolve(true);
      }
    });

    mutationObserver.observe(rootElement, {
      childList: true,
      subtree: true,
    });
  });
}
